import { FormControlLabel } from '@mui/material';
import { useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useConfig } from '@revenuewell/logic-config';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons';
import { OrangeGreenSwitch } from 'apps/wrapper/src/components/shared/switch/switch.styles';
import { useEventBus } from 'apps/wrapper/src/hooks/use-event-bus';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import moment from 'moment-timezone';
import { FC, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useApptRequestsWidget } from '../../hooks/use-appt-requests-widget';
import { useRescheduleRequestView } from '../../hooks/use-reschedule-request-view';
import { AiTranscriptSection } from '../../shared/ai-transcript/ai-transcript-section';
import { AppointmentDetailsSection } from '../../shared/appointment-details/appointment-details-section';
import { InsuranceInfoSection } from '../../shared/insurance-info-section';
import { NotesSection } from '../../shared/notes-section/notes-section';
import { PmsStatusSection } from '../../shared/pms-status/pms-status-section';
import { Page, PageContent, PageTitle, Toolbar } from '../styles';
import { RescheduleRequestViewSkeleton } from './reschedule-request.skeleton';
import { PatientDetailsSection } from './sections/patient-details/patient-details-section';

export const RescheduleRequestView: FC = () => {
  const { config } = useConfig();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const { showError, showSuccess } = useNotification();
  const { publishToIframes, publish } = useEventBus();
  const { locationId, goBack, markRequestAsRead } = useApptRequestsWidget();
  const { isLoading, requestDetails, originalAppointment, tempAppointment, isEditable } = useRescheduleRequestView();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(false);

  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  const isApprovedRequest = useMemo(() => {
    return (
      requestDetails &&
      (requestDetails.status === AppointmentRequestStatus.Unconfirmed ||
        requestDetails.status === AppointmentRequestStatus.Confirmed)
    );
  }, [requestDetails]);

  const isDismissedRequest = useMemo(() => {
    return (
      requestDetails &&
      (requestDetails.status === AppointmentRequestStatus.Deleted ||
        requestDetails.status === AppointmentRequestStatus.Cancelled)
    );
  }, [requestDetails]);

  useEffect(() => {
    if (!requestDetails) return;

    publishToIframes({ messageType: 'appt-request-reset' });
    markRequestAsRead(requestDetails.id);
  }, [requestDetails]);

  const handleRescheduleClick = async () => {
    if (!originalAppointment || !tempAppointment) return;

    try {
      setIsRescheduling(true);
      await appointmentService.acceptRescheduleAppointment(requestDetails.id, locationId);
      publishToIframes({ messageType: 'appt-request-reset' });
      publishToIframes({ messageType: 'appt-request-refresh' });
      showSuccess('Appointment rescheduled');
      goBack(true);

      if (notifyPatient) {
        const message = `Hello ${
          requestDetails.patient?.firstName
        }, your appointment has been successfully rescheduled from ${moment(originalAppointment.dateStart).format(
          'MMMM Do YYYY, h:mm a'
        )} to ${moment(tempAppointment.dateStart).format('MMMM Do YYYY, h:mm a')}.`;

        publish({
          messageType: 'inboxCommunications',
          data: {
            patientId: requestDetails.patient?.id,
            phoneNumber: requestDetails.patient?.phone,
            locationId,
            message
          }
        });
      }
    } catch (error: any) {
      if (error?.status == 409) showError('Time slot is already booked.');
      else showError('Unable to reschedule appointment.');
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleDismissRequestClick = async () => {
    try {
      setIsDismissing(true);
      const claims = await getClaims();

      await appointmentService.dismissAppointmentRequest(requestDetails.id, locationId, claims.masterAccountId);
      publishToIframes({ messageType: 'appt-request-reset' });
      publishToIframes({ messageType: 'appt-request-refresh' });
      showSuccess('Appointment request dismissed');

      if (notifyPatient && requestDetails.patient?.phone && originalAppointment) {
        const originalTime = moment(originalAppointment.dateStart).format('MMMM Do YYYY, h:mm a');
        const message = `Hello ${requestDetails.patient?.firstName}, your reschedule request was not processed and your original appointment at ${originalTime} remains scheduled.`;

        publish({
          messageType: 'inboxCommunications',
          data: {
            patientId: requestDetails.patient?.id,
            phoneNumber: requestDetails.patient?.phone,
            locationId,
            message
          }
        });
      }

      goBack(true);
    } catch {
      showError('Unable to dismiss appointment request');
    } finally {
      setIsDismissing(false);
    }
  };

  if (isLoading) return <RescheduleRequestViewSkeleton />;

  return (
    <Page>
      <PageTitle>Reschedule Request</PageTitle>
      <PageContent sx={{ margin: '0' }}>
        <Scrollbars autoHide>
          <PatientDetailsSection />
          <InsuranceInfoSection insuranceInfo={requestDetails.insuranceInfo} />
          {tempAppointment && originalAppointment && (
            <AppointmentDetailsSection
              title="New Appointment"
              isLoading={isLoading}
              appointmentId={isApprovedRequest ? originalAppointment.appointmentId : tempAppointment.appointmentId}
              startTime={isApprovedRequest ? originalAppointment.dateStart : tempAppointment.dateStart}
              operatoryName={isApprovedRequest ? originalAppointment.operatoryName : tempAppointment.operatoryName}
              providerName={isApprovedRequest ? originalAppointment.providerName : tempAppointment.providerName}
              hideControls={isDismissedRequest}
            />
          )}
          {tempAppointment && originalAppointment && (
            <AppointmentDetailsSection
              title="Original Appointment"
              isLoading={isLoading}
              appointmentId={originalAppointment.appointmentId}
              startTime={isApprovedRequest ? tempAppointment.dateStart : originalAppointment.dateStart}
              operatoryName={originalAppointment.operatoryName}
              providerName={originalAppointment.providerName}
              hideControls={isDismissedRequest || isApprovedRequest}
            />
          )}
          <AiTranscriptSection
            isLoading={isLoading}
            caseId={requestDetails.milaCaseId}
            patientId={requestDetails.patient?.id}
          />
          {(requestDetails.isDirectlyBooked ||
            requestDetails.status === AppointmentRequestStatus.Confirmed ||
            isLoading) && (
            <PmsStatusSection
              isLoading={isLoading}
              pmsWritebackStatus={requestDetails.pmsWritebackStatus}
              appointmentId={requestDetails.id}
            />
          )}
          <NotesSection locationId={locationId} requestDetails={requestDetails} />
        </Scrollbars>
        <Toolbar sx={{ flexDirection: 'column', borderTop: 'none' }}>
          {(requestDetails.status == AppointmentRequestStatus.New ||
            requestDetails.status == AppointmentRequestStatus.Pending) &&
            requestDetails.patient?.phone && (
              <FormControlLabel
                label="Notify patient"
                control={
                  <OrangeGreenSwitch
                    color="default"
                    checked={notifyPatient}
                    onChange={() => setNotifyPatient(!notifyPatient)}
                  />
                }
              />
            )}
          <PrimaryButton
            loading={isRescheduling}
            disabled={isLoading || isDismissing || !isEditable}
            onClick={handleRescheduleClick}
            sx={{ marginBottom: '15px' }}
            disableElevation
          >
            Update Schedule
          </PrimaryButton>
          <SecondaryButton
            loading={isDismissing}
            disabled={isLoading || isRescheduling || !isEditable}
            onClick={handleDismissRequestClick}
            disableElevation
          >
            Dismiss
          </SecondaryButton>
        </Toolbar>
      </PageContent>
    </Page>
  );
};
